import {
  getSpecialization,
  getUniqueSpecIdForRole,
} from 'app/frontend/base/services/characterValues'
import {
  EventActivityParticipantEntry,
  EventActivityParticipantSelection,
} from './eventActivityParticipantEntry'

type ResolveParticipantEntryInput = {
  participant: wowaudit.EventParticipant
  activityIds: number[]
  teamMembers: wowaudit.TeamMembership[]
  rosterCharacterById?: Record<number, wowaudit.CharacterReference>
}

type ResolveParticipantEntriesInput = {
  participants: wowaudit.EventParticipant[]
  activityIds: number[]
  teamMembers: wowaudit.TeamMembership[]
}

const resolveSelectionCharacter = (
  participant: wowaudit.EventParticipant,
  availableTeamMemberships: wowaudit.TeamMembership[],
  rosterCharacterById: Record<number, wowaudit.CharacterReference> | undefined,
  characterId: number,
) => {
  if (
    participant.altCharacterId != null &&
    participant.altCharacterId === characterId &&
    participant.altCharacterReference
  ) {
    return participant.altCharacterReference
  }

  if (participant.characterId === characterId) {
    return participant.characterReference
  }

  return (
    availableTeamMemberships
      .map((teamMembership) => teamMembership.characterReference)
      .find((character) => character.id === characterId) ||
    rosterCharacterById?.[characterId] ||
    participant.characterReference
  )
}

const resolveCompatibleSpecId = (
  explicitSpecId: number | null | undefined,
  fallbackSpecId: number | null | undefined,
  role: string,
  classId?: number,
): number | null => {
  if (explicitSpecId != null) return explicitSpecId
  if (
    fallbackSpecId != null &&
    getSpecialization(String(fallbackSpecId))?.default_role === role
  ) {
    return fallbackSpecId
  }
  return getUniqueSpecIdForRole(classId, role)
}

export const resolveEventParticipantEntry = ({
  participant,
  activityIds,
  teamMembers,
  rosterCharacterById,
}: ResolveParticipantEntryInput): EventActivityParticipantEntry => {
  const member = participant.teamMembership
  const role = participant.role || member?.role || 'melee'
  const participantUserId = participant.characterReference.userId
  const availableTeamMemberships = participantUserId
    ? teamMembers.filter(
        (teamMembership) =>
          teamMembership.characterReference.userId === participantUserId,
      )
    : []
  const characterId =
    participant.altCharacterId ??
    participant.characterId ??
    participant.characterReference.id
  const defaultCharacter = resolveSelectionCharacter(
    participant,
    availableTeamMemberships,
    rosterCharacterById,
    characterId,
  )
  const specId = resolveCompatibleSpecId(
    participant.specId,
    member?.specId,
    role,
    defaultCharacter.classId,
  )
  const selections: Record<number, EventActivityParticipantSelection> = {}

  activityIds.forEach((activityId) => {
    const override = participant.selections[activityId.toString()]
    const selectionRole = override?.role ?? role
    const selectionCharacterId = override?.character_id ?? characterId
    const selectionCharacter =
      selectionCharacterId === characterId
        ? defaultCharacter
        : resolveSelectionCharacter(
            participant,
            availableTeamMemberships,
            rosterCharacterById,
            selectionCharacterId,
          )
    selections[activityId] = {
      selected:
        override?.selected ?? participant.effectiveDefaultSelectionStatus,
      role: selectionRole,
      specId: resolveCompatibleSpecId(
        override?.spec_id ?? participant.specId,
        member?.specId,
        selectionRole,
        selectionCharacter.classId,
      ),
      characterId: selectionCharacterId,
    }
  })
  const selected =
    activityIds.length > 0 &&
    activityIds.every((activityId) => selections[activityId].selected)

  return {
    participant,
    selected,
    role,
    specId,
    characterId,
    availableTeamMemberships,
    rosterCharacterById,
    selections,
  }
}

export const resolveEventParticipantEntries = ({
  participants,
  activityIds,
  teamMembers,
}: ResolveParticipantEntriesInput): EventActivityParticipantEntry[] => {
  const rosterCharacterById = teamMembers.reduce<
    Record<number, wowaudit.CharacterReference>
  >((acc, teamMembership) => {
    acc[teamMembership.characterReference.id] =
      teamMembership.characterReference
    return acc
  }, {})
  return participants.map((participant) =>
    resolveEventParticipantEntry({
      participant,
      activityIds,
      teamMembers,
      rosterCharacterById,
    }),
  )
}

export const countSelectedActivities = (
  entry: EventActivityParticipantEntry,
  activeActivityIds: Set<number>,
) =>
  Object.entries(entry.selections).filter(
    ([activityId, selection]) =>
      activeActivityIds.has(Number(activityId)) && selection.selected,
  ).length

export const resolveEventParticipantCharacter = (
  entry: EventActivityParticipantEntry,
  activityId?: number,
) => {
  const selectedCharacterId =
    activityId && entry.selections[activityId]
      ? entry.selections[activityId].characterId
      : entry.characterId
  return resolveSelectionCharacter(
    entry.participant,
    entry.availableTeamMemberships,
    entry.rosterCharacterById,
    selectedCharacterId,
  )
}
